import { useCallback, useRef, useState } from 'react';
import {
    isFlick,
    isSidewaysClaimed,
    liftThreshold,
    readGesture,
    startsGesture,
    swipeThreshold,
} from './cardGesture';

/**
 * The two gestures a plant card answers (SPEC-025 D-6), on one axis lock.
 *
 * Sideways deals the next card of the category; upward lifts the card open into
 * the whole plant. They are one hook rather than two because they are one
 * decision: a gesture has ONE axis, decided once, and two hooks racing to claim
 * the same pointer is the bug the lock exists to prevent.
 *
 * What the vertical axis MEANS is the caller's, through `verticalPolicy`. On an
 * expanded card vertical is the page scrolling and the gesture abandons itself,
 * which is all this hook did with vertical when it was `useSwipeNavigation`
 * (SPEC-007 D-3). On a collapsed card there is nothing underneath to scroll, and
 * vertical is the lift.
 *
 * Design constraints this shape exists to satisfy:
 *
 * - POINTER EVENTS ONLY. A finger, a mouse and a pen all arrive here, on one
 *   code path, which is why the desktop project proves the same code the phone
 *   projects do. The habb.me pwa's week swipe carries a touch handler, a mouse
 *   handler, a pointer handler and a wheel handler saying four versions of the
 *   same sentence; that is accretion, not a requirement.
 * - NOTHING IS PREVENTED. There is no native horizontal pan on this page to
 *   suppress, and calling `preventDefault` on a move is exactly what would fight
 *   the vertical scrolling the axis lock exists to protect. The gesture watches;
 *   it does not intercept.
 * - The AXIS IS LOCKED ONCE, on the first movement worth reading. A gesture that
 *   keeps changing its mind is one that steals the last 10px of a flick down the
 *   page. Under `verticalPolicy: 'scroll'` a vertical lock abandons the gesture
 *   outright, so nothing this hook does shows up in the reader's scrolling.
 * - The caller gets the LIVE gesture, not just the outcome. A swipe that only
 *   announces itself by navigating is a swipe nobody trusts: the card follows
 *   the finger and the stack behind it scales, both off `progress`, while the
 *   finger is still down (SPEC-025 D-5).
 * - Where sideways ALREADY MEANS SOMETHING, this yields. Two rails on this page
 *   scroll horizontally, and the rule is computed rather than told their class
 *   names, so a third one added later inherits it.
 */

/**
 * @param {(direction: 'forward'|'back') => void} onStep - taken on release, once
 *   a horizontal gesture has crossed the threshold.
 * @param {(direction: 'up'|'down') => void} [onLift] - taken on release, once a
 *   vertical gesture has crossed the lift threshold. Only ever called under
 *   `verticalPolicy: 'lift'`.
 * @param {string} [startsWithin] - a selector the press must land inside for a
 *   gesture to begin at all. Without it the whole element answers, which is what
 *   a page-wide swipe wants; with it the gesture has a HANDLE, and everything
 *   else on the element is content the reader can touch without moving anything.
 * @param {'lift'|'scroll'|(() => 'lift'|'scroll')} [verticalPolicy] - what
 *   vertical MEANS here. `'scroll'` (the default) abandons a vertical gesture the
 *   moment its axis is known, so the reader's scrolling is untouched.
 *
 *   A FUNCTION is read once, at pointer-down, is given the press that started
 *   the gesture, and is held for that gesture. An opened card wants the pull-down
 *   that closes it only in some places — which is a question about the moment the
 *   finger lands and about WHERE it landed. Asking it per frame would let a
 *   gesture change its mind halfway, which is the thing the axis lock exists to
 *   prevent.
 *
 * Both policies are entirely the caller's: this hook reads pointers and reports
 * gestures, and knows nothing about plants.
 */
/**
 * The clock the flick is measured against.
 *
 * `performance.now()` rather than `event.timeStamp`: a synthesised pointer event
 * can carry a zero or a repeated timestamp, and a flick measured against those
 * is either infinitely fast or never happened. This is the same wall clock for
 * both ends of the gesture whatever produced the events.
 */
function now() {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

export default function useCardGestures({ onStep, onLift, startsWithin, verticalPolicy = 'scroll' }) {
    // Held in a ref so the pointer handlers stay stable across renders, the way
    // the gesture itself is. They are attached to a card that re-renders on
    // every frame of a drag.
    const policyRef = useRef(verticalPolicy);

    policyRef.current = verticalPolicy;

    const readPolicy = useCallback((event) => {
        const policy = policyRef.current;

        return typeof policy === 'function' ? policy(event) : policy;
    }, []);

    const gestureRef = useRef(null);
    const [gesture, setGesture] = useState(null);

    const end = useCallback((event, { commit }) => {
        const started = gestureRef.current;

        gestureRef.current = null;
        setGesture(null);

        if (!started || started.pointerId !== event.pointerId) {
            return;
        }

        const dx = event.clientX - started.x;
        const dy = event.clientY - started.y;
        const read = readGesture({ dx, dy, threshold: started.threshold, lift: started.lift });

        if (!commit || !read) {
            return;
        }

        // Far enough, OR fast enough. A flick is a gesture nobody meant to stop
        // short, and holding it to the same distance as a deliberate drag is how
        // a deck comes to feel like it is resisting the reader.
        const flicked = isFlick({
            distance: read.axis === 'horizontal' ? dx : dy,
            elapsedMs: now() - started.at,
        });

        if (!read.isReady && !flicked) {
            return;
        }

        if (read.axis === 'horizontal') {
            onStep(read.direction);
        } else if (started.verticalPolicy === 'lift') {
            onLift?.(read.direction);
        }
    }, [onLift, onStep]);

    const onPointerDown = useCallback((event) => {
        if (!startsGesture(event) || isSidewaysClaimed(event.target, event.currentTarget)) {
            return;
        }

        // Outside the handle, a press is just a press.
        if (startsWithin && !event.target.closest?.(startsWithin)) {
            return;
        }

        gestureRef.current = {
            pointerId: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            threshold: swipeThreshold(event.currentTarget.clientWidth),
            lift: liftThreshold(event.currentTarget.clientHeight),
            // Asked once, when the finger lands, and held for this gesture.
            verticalPolicy: readPolicy(event),
            at: now(),
        };
    }, [readPolicy, startsWithin]);

    const onPointerMove = useCallback((event) => {
        const started = gestureRef.current;

        if (!started || started.pointerId !== event.pointerId) {
            return;
        }

        const read = readGesture({
            dx: event.clientX - started.x,
            dy: event.clientY - started.y,
            threshold: started.threshold,
            lift: started.lift,
        });

        if (!read) {
            return;
        }

        if (read.axis === 'vertical' && started.verticalPolicy !== 'lift') {
            // Abandoned, not paused: the reader is scrolling the page, and
            // nothing this hook does may show up in that.
            gestureRef.current = null;
            setGesture(null);

            return;
        }

        // Captured only once the axis is known, so a gesture that turns out to
        // be a scroll never had its events taken away from the page.
        if (!started.captured && typeof event.currentTarget.setPointerCapture === 'function') {
            started.captured = true;

            try {
                event.currentTarget.setPointerCapture(event.pointerId);
            } catch {
                // Safari throws for a pointer already released. Nothing to do:
                // the gesture still works, it just is not captured.
            }
        }

        setGesture((current) => (
            current
                && current.axis === read.axis
                && current.direction === read.direction
                && current.progress === read.progress
                ? current
                : {
                    axis: read.axis,
                    direction: read.direction,
                    progress: read.progress,
                    isReady: read.isReady,
                }
        ));
    }, []);

    const onPointerUp = useCallback((event) => end(event, { commit: true }), [end]);
    const onPointerCancel = useCallback((event) => end(event, { commit: false }), [end]);

    return {
        handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel },
        gesture,
    };
}

export {
    AXIS_LOCK_PX,
    isFlick,
    EDGE_GUARD_PX,
    isSidewaysClaimed,
    liftThreshold,
    readGesture,
    startsGesture,
    swipeThreshold,
} from './cardGesture';
